import React from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ReportTemplate, SubcontractorRow } from '../../types/projectReport';
import { formatAmount } from '../../utils/projectReport';
import { NumberInput } from './ReportInputs';

/**
 * KWT section 6, Subcontractors Progress: SAP's subcontract purchase orders (PO type Contract / Service)
 * with their certified A/P invoices before, in and up to the month, refreshed from SAP; the project
 * manager adds the work done period and any other subcontractor.
 */

interface Props {
  rows: SubcontractorRow[];
  onChange: (rows: SubcontractorRow[]) => void;
  template: ReportTemplate;
  disabled?: boolean;
}

const SubcontractorsTable: React.FC<Props> = ({ rows, onChange, template, disabled }) => {
  const patch = (index: number, p: Partial<SubcontractorRow>) => onChange(rows.map((r, i) => (i === index ? { ...r, ...p } : r)));
  const remove = (index: number) => onChange(rows.filter((_r, i) => i !== index));
  const add = () => onChange([...rows, {
    name: '', scope: '', reference: '', value: null, paidPrevious: null, paidThisPeriod: null, paidTotal: null, workDonePeriod: '', source: 'PM', poDocEntry: null,
  }]);
  const amount = (r: SubcontractorRow, i: number, key: 'value' | 'paidPrevious' | 'paidThisPeriod' | 'paidTotal', label: string) => (
    r.source === 'SAP' ? (
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatAmount(r[key], template)}</TableCell>
    ) : (
      <TableCell sx={{ minWidth: 120 }}>
        <NumberInput label="" inputLabel={label} value={r[key]} template={template} disabled={disabled} onChange={(v) => patch(i, { [key]: v } as Partial<SubcontractorRow>)} helperText="" />
      </TableCell>
    )
  );
  const text = (r: SubcontractorRow, i: number, key: 'name' | 'scope' | 'reference', label: string, minWidth: number) => (
    r.source === 'SAP' ? (
      <TableCell sx={{ minWidth }}><Typography variant="body2">{r[key]}</Typography></TableCell>
    ) : (
      <TableCell sx={{ minWidth }}>
        <TextField size="small" fullWidth multiline={key === 'scope'} value={r[key]} disabled={disabled} onChange={(e) => patch(i, { [key]: e.target.value } as Partial<SubcontractorRow>)} inputProps={{ 'aria-label': label, maxLength: key === 'scope' ? 500 : 255 }} />
      </TableCell>
    )
  );

  return (
    <Box sx={{ display: 'grid', gap: 1 }}>
      <TableContainer sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" aria-label="Subcontractors progress">
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2}>No.</TableCell>
              <TableCell rowSpan={2}>Subcontractor</TableCell>
              <TableCell rowSpan={2}>Brief scope</TableCell>
              <TableCell rowSpan={2}>SC/SO/LPO No.</TableCell>
              <TableCell rowSpan={2} align="right">Value</TableCell>
              <TableCell colSpan={3} align="center">Payment (certified)</TableCell>
              <TableCell rowSpan={2}>Work done period</TableCell>
              <TableCell rowSpan={2} />
            </TableRow>
            <TableRow>
              <TableCell align="right">Previous</TableCell>
              <TableCell align="right">This period</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={10}><Typography variant="body2" color="text.secondary">No subcontract in SAP for this project.</Typography></TableCell></TableRow>
            )}
            {rows.map((r, i) => (
              <TableRow key={`${r.source}-${r.poDocEntry ?? i}`}>
                <TableCell>{i + 1}</TableCell>
                {text(r, i, 'name', 'Subcontractor', 180)}
                {text(r, i, 'scope', 'Brief scope', 200)}
                {text(r, i, 'reference', 'SC/SO/LPO number', 150)}
                {amount(r, i, 'value', 'Value')}
                {amount(r, i, 'paidPrevious', 'Paid previously')}
                {amount(r, i, 'paidThisPeriod', 'Paid this period')}
                {amount(r, i, 'paidTotal', 'Paid in total')}
                <TableCell sx={{ minWidth: 140 }}>
                  <TextField size="small" fullWidth value={r.workDonePeriod} disabled={disabled} onChange={(e) => patch(i, { workDonePeriod: e.target.value })} inputProps={{ 'aria-label': 'Work done period', maxLength: 120 }} />
                </TableCell>
                <TableCell>
                  {r.source === 'SAP' ? (
                    <Tooltip title="From SAP: refreshed with the SAP figures"><Chip size="small" label="SAP" color="success" variant="outlined" /></Tooltip>
                  ) : (
                    !disabled && <IconButton aria-label="Remove subcontractor" size="small" onClick={() => remove(i)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!disabled && <Box><Button size="small" startIcon={<AddIcon />} onClick={add}>Add a subcontractor</Button></Box>}
    </Box>
  );
};

export default SubcontractorsTable;
